import moment from "moment";
import { getListRowLocationDisplay } from "./addressUtils";
import { formatRiskSummaryForExport } from "../components/RFIs/RiskSummaryCell";
import { getMyRfisAllPagesForExport } from "../api/api-methods";
import { buildRiskAwareExportData } from "./gridExportAllRecords";
import { RFI_CATALOG_SERVICE_NAMES } from "./rfiCatalogServiceNames";
import { resolveServiceCategoryNameFromLabel } from "./templateToServiceCategory";

/**
 * Toggle server-side sort for `get_rfis` (My RFIs) vs client-side sort on the current page only.
 */
export const MY_RFIS_USE_SERVER_SORT = true;

/**
 * Normalized row for the RFI History-style table (Innovation Lab).
 *
 * When the backend adds new fields, extend `mapMyRfiItemToGridRow` to read them
 * from `item` (e.g. item.gxp, item.risk_score). Location uses structured fields via
 * `getRfiRequestLocationDisplay` (not raw `item.location`, which is the street address).
 */

/**
 * Location column for VQQ list grids — city, state, country (no street), aligned with My Assessments.
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getRfiRequestLocationDisplay(item) {
  const formatted = getListRowLocationDisplay(item, {
    short: false,
    abbvState: false,
  });
  if (formatted) return formatted;

  // Legacy rows before structured location fields existed on get_rfis
  const legacy =
    item?.city_state_country ?? item?.vendor_location ?? item?.vendorLocation ?? "";
  return typeof legacy === "string" ? legacy.trim() : "";
}

/**
 * Full vendor address with the street first: "Street, City, State, Country". Used where a
 * segment has to be identified precisely (segment cards), while grids keep the street-less
 * display above. `location` carries the street the request was created with; when it is
 * missing, or already holds the composed display, this degrades to the street-less form.
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getRfiRequestFullAddressDisplay(item) {
  const rest = getRfiRequestLocationDisplay(item);
  const street = typeof item?.location === "string" ? item.location.trim() : "";
  if (!street) return rest;
  if (!rest) return street;
  if (street.toLowerCase() === rest.toLowerCase()) return rest;
  return `${street}, ${rest}`;
}

export const RFI_TYPE_DILIGENT = "Diligent VQQ";
export const RFI_TYPE_CUSTOM = "Custom VQQ";

/**
 * Label for the Type badge. This is a two-value classifier, Diligent VQQ or Custom VQQ, so
 * the API `type` is honoured only when it is one of those two; anything else (the Details
 * payload has carried the template name here) falls back to `is_global_template`. Never
 * echoes an arbitrary string.
 */
export function getRfiTypeLabelFromItem(item) {
  const fromApi = String(item?.type ?? item?.Type ?? "").trim().toLowerCase();
  if (fromApi === RFI_TYPE_DILIGENT.toLowerCase()) return RFI_TYPE_DILIGENT;
  if (fromApi === RFI_TYPE_CUSTOM.toLowerCase()) return RFI_TYPE_CUSTOM;
  return Boolean(item?.is_global_template) ? RFI_TYPE_DILIGENT : RFI_TYPE_CUSTOM;
}

/** True when UI should use the "Diligent" (purple) badge style for this label. */
export function rfiTypeLabelLooksDiligent(typeLabel) {
  if (!typeLabel) return false;
  return /\bdiligent\b/i.test(String(typeLabel));
}

/** Toolbar Type filter: diligent vs custom, aligned with `getRfiTypeLabelFromItem`. */
export function isDiligentRfiItemForTypeFilter(item) {
  const label = getRfiTypeLabelFromItem(item).toLowerCase();
  if (label.includes("diligent")) return true;
  if (label.includes("custom")) return false;
  return Boolean(item?.is_global_template);
}

/** Default row shape for RFI grids (My RFIs, admin, vendor-requested). */
export const MY_RFI_GRID_EMPTY = {
  vendor: "",
  templateName: "",
  typeLabel: "",
  gxp: "",
  services: "",
  location: "",
  riskSummary: "",
  status: "",
  requestDateRaw: null,
  initialReleaseDateRaw: null,
  completionDateRaw: null,
  dueDateRaw: null,
  requestDateDisplay: "",
  initialReleaseDateDisplay: "",
  completionDateDisplay: "",
  dueDateDisplay: "",
  cycleTime: "",
};

/**
 * Cycle time string from the API (`cycle_time` / `cycleTime`). Empty when missing — do not derive from dates.
 * @param {object|null|undefined} item
 */
export function getRfiCycleTimeFromApi(item) {
  if (!item) return "";
  const v = item.cycle_time ?? item.cycleTime ?? item.CycleTime;
  if (v == null || v === "") return "";
  return String(v).trim();
}

/**
 * Maps a get_rfis row: Release Date = first In Review (initialReleaseDate);
 * Completion Date = questionnaire completed (releaseDate).
 * @param {object|null|undefined} item
 * @returns {typeof MY_RFI_GRID_EMPTY & { raw: object, cycleTime: string }}
 */
export function mapMyRfiItemToGridRow(item) {
  if (!item) {
    return { ...MY_RFI_GRID_EMPTY, raw: {} };
  }

  const templateName = item.name ?? "";
  const typeLabel = getRfiTypeLabelFromItem(item);

  // GxP comes only from the API. A GxP in the template name is part of that name
  // ("Foundation - GCP", "Bioanalytical - GLP"), not a separate value.
  const gxp = String(item.gxp ?? item.gxp_label ?? item.gxpLabel ?? "").trim();

  const location = getRfiRequestLocationDisplay(item);

  const riskSummary = formatRiskSummary(item);

  const requestDateRaw = item.requestDate ?? null;
  const initialReleaseDateRaw =
    item.initialReleaseDate ?? item.initial_release_date ?? null;
  const completionDateRaw =
    item.releaseDate ?? item.releasedDate ?? item.completionDate ?? null;
  const dueDateRaw = item.due_by ?? item.dueBy ?? null;

  const servicesRaw = item.services ?? item.Services;
  const servicesDisplay = normalizeRfiServicesFieldToString(servicesRaw);

  return {
    vendor: item.vendorName ?? "",
    templateName,
    typeLabel,
    gxp,
    services: servicesDisplay,
    location,
    riskSummary,
    status:
      item.status ??
      item.Status ??
      item.request_status ??
      item.requestStatus ??
      "",
    isAutoreleased: Boolean(
      item?.isAutoreleased ?? item?.is_autoreleased
    ),
    autoreleasedAt: item?.autoreleasedAt ?? item?.autoreleased_at ?? null,
    requestDateRaw,
    initialReleaseDateRaw,
    completionDateRaw,
    dueDateRaw,
    requestDateDisplay: formatGridDate(requestDateRaw),
    initialReleaseDateDisplay: formatGridDate(initialReleaseDateRaw),
    completionDateDisplay: formatGridDate(completionDateRaw),
    dueDateDisplay: formatGridDate(dueDateRaw),
    cycleTime: getRfiCycleTimeFromApi(item),
    raw: item,
  };
}

/**
 * RFI list APIs may return `services` as a string, array of labels, or nested objects.
 * Coerce to a single display/sort string (same logic for grid + export + sort).
 */
export function normalizeRfiServicesFieldToString(raw) {
  return normalizeRfiServicesFieldToLabels(raw).join(", ");
}

// Catalog names with a comma of their own ("Recruitment, Screening & Retention").
const COMMA_CATEGORY_NAMES = Array.from(RFI_CATALOG_SERVICE_NAMES)
  .filter((name) => name.includes(","))
  .map((name) => name.toLowerCase());
const MAX_COMMA_CATEGORY_PARTS = Math.max(
  1,
  ...COMMA_CATEGORY_NAMES.map((name) => name.split(",").length)
);

/** Whether a label, a VQQ title included, names a category with a comma in its name. */
function isCommaCategoryLabel(label) {
  const resolved = resolveServiceCategoryNameFromLabel(label) || label;
  return COMMA_CATEGORY_NAMES.includes(resolved.trim().toLowerCase());
}

/** How many comma-separated parts from `start` make up one category name (1 when none). */
function commaCategoryPartsAt(parts, start) {
  for (
    let count = Math.min(parts.length - start, MAX_COMMA_CATEGORY_PARTS);
    count > 1;
    count -= 1
  ) {
    if (isCommaCategoryLabel(parts.slice(start, start + count).join(", "))) {
      return count;
    }
  }
  return 1;
}

/**
 * Split a comma-separated services value into labels, keeping a category whose own
 * name contains a comma whole: "Diligent Recruitment, Screening & Retention" is one
 * template, not "Diligent Recruitment" and "Screening & Retention".
 */
function splitServicesString(value) {
  const parts = value
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);
  const labels = [];
  let index = 0;
  while (index < parts.length) {
    const count = commaCategoryPartsAt(parts, index);
    labels.push(parts.slice(index, index + count).join(", "));
    index += count;
  }
  return labels;
}

/**
 * Normalize RFI service/template values into individual labels suitable for
 * the shared Capabilities badges used by Assessment scope.
 */
export function normalizeRfiServicesFieldToLabels(raw) {
  if (raw == null || raw === "") return [];

  const values = Array.isArray(raw) ? raw : [raw];
  return values
    .flatMap((value) => {
      if (value == null || value === "") return [];
      if (typeof value === "object") {
        const label =
          value.name ?? value.label ?? value.services ?? value.rfi_name ?? "";
        return label === "" ? [] : [String(label).trim()];
      }
      if (typeof value === "string") {
        return splitServicesString(value);
      }
      return [String(value).trim()];
    })
    .filter(Boolean);
}

/** Lowercase string for comparing RFI service/template column rows when sorting. */
export function getRfiServicesSortComparable(item) {
  if (!item) return "";
  const raw = item.services ?? item.Services;
  return normalizeRfiServicesFieldToString(raw).toLowerCase();
}

function formatRiskSummary(item) {
  const rs = item.risk_summary ?? item.riskSummary;
  if (rs != null && String(rs).trim() !== "") return String(rs);

  const score = item.risk_score ?? item.riskScore;
  const level = item.risk_level ?? item.riskLevel;
  if (score != null && level != null) return `${level} (${score}%)`;
  if (score != null) return String(score);
  if (level != null) return String(level);
  return "";
}

/** Short dates aligned with the Innovation Lab grid (M-D-YY) */
export function formatGridDate(value) {
  if (value == null || value === "") return "";
  try {
    const m = moment.utc(value);
    if (!m.isValid() || m.year() < 1901) return "";
    return m.format("M-D-YY");
  } catch {
    return "";
  }
}

export function computeCycleTime(requestDate, releaseDate) {
  if (!requestDate || !releaseDate) return "";
  try {
    const a = new Date(requestDate);
    const b = new Date(releaseDate);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return "";
    const diff = Math.ceil(Math.abs(b - a) / (1000 * 60 * 60 * 24));
    return `${diff} days`;
  } catch {
    return "";
  }
}

/** Sort key extraction from a raw API row (same keys as handleSort on the page) */
export function getSortComparableValue(item, sortKey) {
  if (!item) return null;
  const row = mapMyRfiItemToGridRow(item);

  switch (sortKey) {
    case "sponsor":
      return (item.sponsorName || "").toLowerCase();
    case "vendor":
      return (item.vendorName || "").toLowerCase();
    case "template":
      // "Template" column shows service categories (`services`); sort matches visible badges.
      return getRfiServicesSortComparable(item);
    case "type":
      return getRfiTypeLabelFromItem(item).toLowerCase();
    case "gxp":
      return (row.gxp || "zzz").toLowerCase();
    case "services":
      return getRfiServicesSortComparable(item);
    case "location":
      return (row.location || "zzz").toLowerCase();
    case "risk":
      return parseRiskSortValue(item);
    case "status":
      return (item.status || "").toLowerCase();
    case "requestDate":
      return item.requestDate ? new Date(item.requestDate).getTime() : 0;
    case "releasedDate":
      return item.initialReleaseDate || item.initial_release_date
        ? new Date(
            item.initialReleaseDate || item.initial_release_date
          ).getTime()
        : 0;
    case "completionDate":
      return item.releaseDate || item.releasedDate
        ? new Date(item.releaseDate || item.releasedDate).getTime()
        : 0;
    case "dueDate":
      return item.due_by || item.dueBy
        ? new Date(item.due_by || item.dueBy).getTime()
        : 0;
    case "cycleTime": {
      const s = getRfiCycleTimeFromApi(item);
      if (!s) return "";
      const n = parseInt(String(s).replace(/\D/g, ""), 10);
      return Number.isNaN(n) ? s.toLowerCase() : n;
    }
    default:
      return "";
  }
}

/**
 * Maps RfiGridTable column keys to `sort_by` values for `get_rfis`.
 * Align names with the backend contract when enabling server-side sort.
 */
/** Columns not listed here are not sortable on the server (see RfiGridTable).
 *  14029 — `risk` deliberately omitted: the Risk Summary column is enriched
 *  client-side from a separate /RiskScoring/summaries batch call, so server
 *  sort doesn't apply. RfiGridTable re-sorts client-side when that column is
 *  active. */
export const MY_RFI_API_ORDER_BY_FIELD = {
  vendor: "vendor_name",
  /** Template column shows service categories; server sort must use `services`, not questionnaire title. */
  template: "services",
  gxp: "gxp",
  /** Server sorts by city/state/country (same as the visible Location column), not street address. */
  location: "location",
  status: "status",
  requestDate: "request_date",
  dueDate: "due_by",
  releasedDate: "initial_release_date",
  completionDate: "release_date",
  cycleTime: "cycle_time",
};

/**
 * Maps sort state to API `order_by` / `sort_direction` using a column-key → API field map.
 */
export function sortConfigToApiSortParamsWithFieldMap(fieldMap, sortConfig) {
  const keys = Object.keys(sortConfig || {});
  if (keys.length === 0) {
    return { orderBy: null, sortDirection: null };
  }
  const columnKey = keys[0];
  if (columnKey === "actions") {
    return { orderBy: null, sortDirection: null };
  }
  const orderBy = fieldMap[columnKey];
  if (!orderBy) {
    return { orderBy: null, sortDirection: null };
  }
  const direction = sortConfig[columnKey]?.direction;
  const sortDirection = direction === "asc" ? "asc" : "desc";
  return { orderBy, sortDirection };
}

/**
 * @param {Record<string, { direction?: string }>} sortConfig — single active column (same shape as table state)
 * @returns {{ orderBy: string | null, sortDirection: 'asc' | 'desc' | null }}
 */
export function sortConfigToApiSortParams(sortConfig) {
  return sortConfigToApiSortParamsWithFieldMap(
    MY_RFI_API_ORDER_BY_FIELD,
    sortConfig
  );
}

function parseRiskSortValue(item) {
  const n = item.risk_score ?? item.riskScore;
  if (typeof n === "number") return n;
  if (n != null && !Number.isNaN(Number(n))) return Number(n);
  return -1;
}

function naturalSortCompare(a, b) {
  if (!a && b) return -1;
  if (a && !b) return 1;
  if (!a && !b) return 0;
  const aStr = String(a).trim();
  const bStr = String(b).trim();
  const aParts = aStr.split(/(\d+)/).filter(Boolean);
  const bParts = bStr.split(/(\d+)/).filter(Boolean);
  const minLength = Math.min(aParts.length, bParts.length);
  for (let i = 0; i < minLength; i++) {
    if (!isNaN(aParts[i]) && !isNaN(bParts[i])) {
      const numA = parseInt(aParts[i], 10);
      const numB = parseInt(bParts[i], 10);
      if (numA !== numB) return numA - numB;
    } else if (aParts[i] !== bParts[i]) {
      return aParts[i].localeCompare(bParts[i]);
    }
  }
  return aParts.length - bParts.length;
}

/**
 * Sorts My RFIs API rows using sortConfig (single active column).
 * Used by the page and the RFI table component.
 */
/** GxP abbreviations used in toolbar filters (subset of template-name extraction). */
export const MY_RFI_GXP_FILTER_OPTIONS = [
  { value: "GCP", label: "GCP" },
  { value: "GCLP", label: "GCLP" },
  { value: "GLP", label: "GLP" },
  { value: "GMP", label: "GMP" },
  { value: "GVP", label: "GVP" },
  { value: "GDP", label: "GDP" },
  { value: "GAMP", label: "GAMP" },
];

/**
 * Client-side filters for fields not covered by get_rfis (Type, GxP).
 * When any of these are active, filtering applies to the current page payload only.
 */
export function filterMyRfiItemsByClientFilters(items, selectedIds) {
  if (!items?.length) return [];
  const typeFilters = selectedIds?.type || [];
  const gxpFilters = selectedIds?.gxp || [];
  if (typeFilters.length === 0 && gxpFilters.length === 0) return items;

  return items.filter((item) => {
    if (typeFilters.length > 0) {
      const isDiligent = isDiligentRfiItemForTypeFilter(item);
      const ok = typeFilters.some((t) => {
        if (t === "diligent") return isDiligent;
        if (t === "custom") return !isDiligent;
        return false;
      });
      if (!ok) return false;
    }
    if (gxpFilters.length > 0) {
      const row = mapMyRfiItemToGridRow(item);
      const gxpVal = (row.gxp || "").toUpperCase();
      const ok = gxpFilters.some((code) => {
        const c = String(code).toUpperCase();
        return gxpVal === c || gxpVal.includes(c);
      });
      if (!ok) return false;
    }
    return true;
  });
}

export function sortMyRfiItems(items, sortConfig) {
  if (!items?.length) return [];
  const copy = [...items];
  const keys = Object.keys(sortConfig || {});
  if (keys.length === 0) return copy;
  const key = keys[0];
  const { direction } = sortConfig[key];
  const mult = direction === "asc" ? 1 : -1;

  if (key === "sponsor") {
    return copy.sort((a, b) => {
      const result = naturalSortCompare(
        (a.sponsorName || "").toLowerCase(),
        (b.sponsorName || "").toLowerCase()
      );
      return result * mult;
    });
  }

  if (key === "vendor") {
    return copy.sort((a, b) => {
      const result = naturalSortCompare(
        (a.vendorName || "").toLowerCase(),
        (b.vendorName || "").toLowerCase()
      );
      return result * mult;
    });
  }

  if (key === "services" || key === "template") {
    return copy.sort((a, b) => {
      const result = naturalSortCompare(
        getRfiServicesSortComparable(a),
        getRfiServicesSortComparable(b)
      );
      return result * mult;
    });
  }

  if (key === "cycleTime") {
    return copy.sort((a, b) => {
      const valueA = getRfiCycleTimeFromApi(a);
      const valueB = getRfiCycleTimeFromApi(b);
      const isEmptyA = !valueA;
      const isEmptyB = !valueB;
      if (isEmptyA && !isEmptyB) return direction === "asc" ? 1 : -1;
      if (!isEmptyA && isEmptyB) return direction === "asc" ? -1 : 1;
      if (isEmptyA && isEmptyB) return 0;
      const result = naturalSortCompare(
        valueA.toLowerCase(),
        valueB.toLowerCase()
      );
      return direction === "asc" ? result : -result;
    });
  }

  return copy.sort((a, b) => {
    const va = getSortComparableValue(a, key);
    const vb = getSortComparableValue(b, key);
    if (va === vb) return 0;
    if (typeof va === "number" && typeof vb === "number") {
      return va < vb ? -mult : mult;
    }
    const sa = String(va ?? "");
    const sb = String(vb ?? "");
    if (sa < sb) return -mult;
    if (sa > sb) return mult;
    return 0;
  });
}

export const MY_RFI_EXPORT_HEADERS = [
  "Vendor",
  "Type",
  "GxP",
  "Template",
  "Location",
  "Risk Summary",
  "Status",
  "Request Date",
  "Due Date",
  "Release Date",
  "Completion Date",
  "Cycle Time",
];

/**
 * @param {object} row — grid row from mapMyRfiItemToGridRow
 * @param {object} [riskSummary] — 14029 published Risk Summary for this row's
 *   sponsor_rfi_response_id (from /RiskScoring/summaries). Empty when no
 *   published snapshot applies; export cell renders blank per AC.
 */
export function mapGridRowToExportRecord(row, riskSummary = null) {
  return {
    Vendor: row.vendor,
    Type: row.typeLabel,
    "GxP": row.gxp,
    Template: row.services,
    Location: row.location || "",
    "Risk Summary": formatRiskSummaryForExport(riskSummary),
    Status: row.status,
    "Request Date": row.requestDateDisplay || "",
    "Due Date": row.dueDateDisplay || "",
    "Release Date": row.initialReleaseDateDisplay || "",
    "Completion Date": row.completionDateDisplay || "",
    "Cycle Time": row.cycleTime || "",
  };
}

/**
 * Export-menu data builder for the sponsor "My VQQs" grid: fetches every page
 * of the current filter/sort selection and resolves Risk Summary cells for the
 * full export set, not just the visible page. Lives with the grid's mappers so
 * the wiring stays unit-testable; the page passes its live filter state in via
 * useGridExportRegistration.
 */
export const buildMyRfisExportData = ({
  searchQuery,
  selectedIds,
  serverSortParams,
  sortConfig,
  dueDateFilter,
  includeRisk = true,
}) =>
  buildRiskAwareExportData({
    includeRisk,
    fetchAllPages: () =>
      getMyRfisAllPagesForExport({
        searchQuery,
        provider: [],
        templates: selectedIds.templates,
        status: selectedIds.status,
        services: selectedIds.services,
        vendors: selectedIds.provider,
        sortOptions: serverSortParams || {},
        dueDateFilter,
      }),
    refineItems: (items) => {
      const afterLocal = filterMyRfiItemsByClientFilters(items, selectedIds);
      return MY_RFIS_USE_SERVER_SORT
        ? afterLocal
        : sortMyRfiItems(afterLocal, sortConfig);
    },
    mapRecordWithSummary: (item, summary) =>
      mapGridRowToExportRecord(mapMyRfiItemToGridRow(item), summary),
    headers: MY_RFI_EXPORT_HEADERS,
  });
